import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import WrapFieldWithAsterisk from '~/components/core/Molecules/WrapFieldWithAsterisk/WrapFieldWithAsterisk';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useCms } from '~/components/hooks/useCms';
import useIsConfigurationFieldSupportedByCategories, {
  IncidentTypeOverrideProvider,
} from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import useSafeFormikContext from '~/components/hooks/useSafeFormikContext';

import CardDialog from '../../../../CardDialog';
import { ErrorHelperTextFormik } from '../../../../core/Formik/ErrorHelperTextFormik';
import { useIncidentConfiguration } from '../../../../hooks/useIncidentConfiguration';
import { useLob } from '../../../../hooks/useLob';
import { useLobConfiguration } from '../../../../hooks/useLobConfiguration';
import { prepareConfiguredFields } from '../../../../IncidentConfiguration/ConfiguredFields';
import { CustomFieldsContextProvider } from '../../../../IncidentConfiguration/CustomFieldsContext';
import { usePolicy } from '../../../../PolicyContainer';
import { InvolvedGenericParty } from '../InvolvedGenericParty';
import {
  getInvolvedGenericValidationFields,
  InvolvedGenericFragment,
} from '../InvolvedGenericParty/InvolvedGenericFragment';

import { FIRST_PARTY_VALUES } from '.';

import { useStyles } from '../../../../../assets/styles';
import styles from '../index.module.scss';

const GeneralInvolvedPartiesCard = ({
  firstPartyValues,
  thirdPartyValues,
  onFetchInvolvedDetails,
  onAddParty,
  onSaveParty,
  onDeleteFirstParty,
  onDeleteThirdParty,
  isFnol = false,
}) => {
  const { classes } = useStyles();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { policy } = usePolicy();
  const { lob } = useLob();
  const { lobConfigurationsDict } = useLobConfiguration();
  const { userOrganization } = useCms();
  const formikContext = useSafeFormikContext();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const isAsteriskEnabled = Boolean(
    userOrganization.configuration?.fnol_configuration?.show_asterisk_on_mandatory_fields
  );

  // On FNOL the wizard formik holds the incident type, but each party opens an isolated Formik,
  // so hand it across that boundary. On the claim page there is no wizard formik - leaving the
  // override unset lets the hook fall back to claim.incident.
  const incidentTypeOverride = formikContext?.values?.incident_type
    ? {
        incidentType: formikContext.values.incident_type,
        incidentSubType: formikContext.values.incident_sub_type,
      }
    : undefined;

  const containerStyle = {
    border: '1px solid #D0D0D0',
    marginBottom: '24px',
    marginTop: '25px',
  };

  const headerStyle = {
    padding: '24px 12px 14px 24px',
  };

  const incidentConfigParties = incidentConfiguration?.involved_parties || {};

  const isInvolvedParty = (party, isFirstParty) => party?.active && party?.is_first_party === isFirstParty;

  const incidentConfigFirstPartyKeys = Object.keys(incidentConfigParties).filter((key) =>
    isInvolvedParty(incidentConfigParties[key], true)
  );
  const incidentConfigThirdPartyKeys = Object.keys(incidentConfigParties).filter((key) =>
    isInvolvedParty(incidentConfigParties[key], false)
  );

  // A claim must always keep at least one first party, so the last one can
  // never be deleted regardless of how many first party types are configured.
  const shouldAllowFirstPartyDeletion = () => firstPartyValues.length > 1;

  // The error helpers below are Formik/Yup validation output, so they are gated on the card
  // actually being mounted inside a form rather than on isFnol: the claim page renders this card
  // with plain useState and has no field to validate. useSafeFormikContext returns {} out of context.
  const isInsideForm = !isEmpty(formikContext);

  // Helper function to determine if Add button should be shown for a first party type
  const shouldShowAddButton = (partyTypeKey) => {
    const lobInvolvedPartiesConfig = get(lobConfigurationsDict, `${lob}.involved_parties`, []);
    const lobPartyConfig = lobInvolvedPartiesConfig.find((party) => party.id === partyTypeKey);
    const isMultipleAllowed = lobPartyConfig?.is_multiple_first_parties;

    // If multiple instances are allowed, always show Add button
    if (isMultipleAllowed) {
      return true;
    }

    // If multiple instances are NOT allowed, only show Add button if no instances exist yet
    const existingPartiesOfThisType = firstPartyValues.filter(
      (party) => party.general_claim_involved_type === partyTypeKey
    );
    return existingPartiesOfThisType.length === 0;
  };

  return (
    <IncidentTypeOverrideProvider value={incidentTypeOverride}>
      {incidentConfigFirstPartyKeys.length > 0 && (
        <CardDialog title="First Parties" headerStyle={headerStyle} containerStyle={containerStyle}>
          <Grid item xs={12}>
            {incidentConfigFirstPartyKeys.map((incidentConfigFirstPartyKey) => (
              <CustomFieldsContextProvider
                key={incidentConfigFirstPartyKey}
                customFieldsConfigurations={prepareConfiguredFields(
                  get(incidentConfigParties, `${incidentConfigFirstPartyKey}.configured_fields`, []),
                  isFieldSupportedByCategories
                )}
              >
                {firstPartyValues
                  .filter(
                    (firstPartyValue) => firstPartyValue.general_claim_involved_type === incidentConfigFirstPartyKey
                  )
                  .map((firstPartyInvolved, curIdx) => {
                    const originalIndex = firstPartyValues.findIndex((p) => p.id === firstPartyInvolved.id);
                    return (
                      <div className={styles.cardSpace} key={firstPartyInvolved.id}>
                        <InvolvedGenericParty
                          title={
                            <WrapFieldWithAsterisk showAsterisk={isFnol && isAsteriskEnabled}>
                              <div>{`${incidentConfigParties[incidentConfigFirstPartyKey].desc} (${curIdx + 1})`}</div>
                            </WrapFieldWithAsterisk>
                          }
                          propertyLabel={incidentConfigParties[incidentConfigFirstPartyKey].desc}
                          involvedGeneric={firstPartyInvolved}
                          onFetchInvolvedDetails={onFetchInvolvedDetails}
                          onSaveInvolvedGenericParty={async (firstParty) =>
                            await onSaveParty(firstParty, incidentConfigFirstPartyKey)
                          }
                          onDeleteInvolvedGenericParty={
                            onDeleteFirstParty && shouldAllowFirstPartyDeletion()
                              ? async () => await onDeleteFirstParty(firstPartyInvolved)
                              : null
                          }
                          InvolvedGenericFragmentOverride={
                            <InvolvedGenericFragment involvedParty={incidentConfigFirstPartyKey} />
                          }
                          shouldCheckDeletePermission={!isFnol}
                          involvedGenericValidationFields={getInvolvedGenericValidationFields(
                            incidentConfiguration,
                            incidentConfigFirstPartyKey,
                            policy,
                            isFieldSupportedByCategories
                          )}
                          errorHelper={
                            isInsideForm ? (
                              <ErrorHelperTextFormik
                                id={`${FIRST_PARTY_VALUES}.${originalIndex}.involved_details_filled`}
                              />
                            ) : undefined
                          }
                        />
                      </div>
                    );
                  })}
                {shouldShowAddButton(incidentConfigFirstPartyKey) && (
                  <div className={classes.buttonsContainer}>
                    <Button onClick={() => onAddParty(incidentConfigFirstPartyKey)}>
                      <AddIcon />
                      Add {incidentConfigParties[incidentConfigFirstPartyKey].desc}{' '}
                    </Button>
                  </div>
                )}
              </CustomFieldsContextProvider>
            ))}

            {isInsideForm && isEmpty(firstPartyValues) && <ErrorHelperTextFormik id={FIRST_PARTY_VALUES} />}
          </Grid>
        </CardDialog>
      )}

      {incidentConfigThirdPartyKeys.length > 0 && (
        <CardDialog title="Third Parties" headerStyle={headerStyle} containerStyle={containerStyle}>
          <Grid item xs={12}>
            {incidentConfigThirdPartyKeys.map((incidentConfigThirdPartyKey) => (
              <CustomFieldsContextProvider
                key={incidentConfigThirdPartyKey}
                customFieldsConfigurations={prepareConfiguredFields(
                  get(incidentConfigParties, `${incidentConfigThirdPartyKey}.configured_fields`, []),
                  isFieldSupportedByCategories
                )}
              >
                {thirdPartyValues
                  .filter(
                    (thirdPartyValue) => thirdPartyValue.general_claim_involved_type === incidentConfigThirdPartyKey
                  )
                  .map((thirdPartyInvolved, curIdx) => (
                    <div className={styles.cardSpace} key={thirdPartyInvolved.id}>
                      <InvolvedGenericParty
                        title={`${incidentConfigParties[incidentConfigThirdPartyKey].desc} (${curIdx + 1})`}
                        propertyLabel={incidentConfigParties[incidentConfigThirdPartyKey].desc}
                        involvedGeneric={thirdPartyInvolved}
                        onFetchInvolvedDetails={onFetchInvolvedDetails}
                        onSaveInvolvedGenericParty={async (thirdParty) =>
                          await onSaveParty(thirdParty, incidentConfigThirdPartyKey)
                        }
                        onDeleteInvolvedGenericParty={
                          onDeleteThirdParty && (async () => await onDeleteThirdParty(thirdPartyInvolved))
                        }
                        InvolvedGenericFragmentOverride={
                          <InvolvedGenericFragment involvedParty={incidentConfigThirdPartyKey} />
                        }
                        shouldCheckDeletePermission={!isFnol}
                        involvedGenericValidationFields={getInvolvedGenericValidationFields(
                          incidentConfiguration,
                          incidentConfigThirdPartyKey,
                          policy,
                          isFieldSupportedByCategories
                        )}
                        // Third parties don't have external Yup validation per involved party as we assume the dialog will validate itself - no need to define errorHelper
                      />
                    </div>
                  ))}
                <div className={classes.buttonsContainer}>
                  <Button onClick={() => onAddParty(incidentConfigThirdPartyKey)}>
                    <AddIcon />
                    Add {incidentConfigParties[incidentConfigThirdPartyKey].desc}{' '}
                  </Button>
                </div>
              </CustomFieldsContextProvider>
            ))}
          </Grid>
        </CardDialog>
      )}
    </IncidentTypeOverrideProvider>
  );
};

GeneralInvolvedPartiesCard.propTypes = {
  firstPartyValues: PropTypes.array.isRequired,
  thirdPartyValues: PropTypes.array.isRequired,
  onFetchInvolvedDetails: PropTypes.func.isRequired,
  onAddParty: PropTypes.func.isRequired,
  onSaveParty: PropTypes.func.isRequired,
  onDeleteFirstParty: PropTypes.func,
  onDeleteThirdParty: PropTypes.func,
  isFnol: PropTypes.bool,
};

export default GeneralInvolvedPartiesCard;
